export type PaymentSessionStatus =
  | 'CREATED'
  | 'AWAITING_PAYMENT'
  | 'COMPLETED'
  | 'EXPIRED'
  | 'CANCELLED'
  | 'FAILED';

export type PaymentSessionPhase = 'IDLE' | 'PAYING';

/** Server checkout method — locks after checkout start. */
export type ApiCheckoutMethod = 'QR' | 'MANUAL' | 'MOBILE_APP';

/** SDK init — omit to allow both QR and Manual. */
export type CheckoutPolicy = 'QR' | 'MANUAL';

/** SDK init — phone-only app handoff preference. */
export type MobileAppPolicy = 'off' | 'option' | 'enforce';

/** Explicit checkout restriction returned by the API. */
export type ApiCheckoutPolicy = 'QR' | 'MANUAL';

/** Bound checkout policy — `null` means full chooser (QR + manual). */
export type BoundCheckoutPolicy = ApiCheckoutPolicy | null;

/** Bound mobile app policy returned by the API. */
export type ApiMobileAppPolicy = 'OFF' | 'OPTION' | 'ENFORCE';

export type BindCheckoutPolicyRequest = {
  checkoutPolicy?: ApiCheckoutPolicy;
  mobileAppPolicy?: ApiMobileAppPolicy;
};

export type CheckoutSurface = 'phone' | 'desktop';

export type StartCheckoutRequest = {
  method: ApiCheckoutMethod;
  surface: CheckoutSurface;
};

export type ZippyMobileAppConfig = {
  policy?: MobileAppPolicy;
};

export type PaymentSessionPayer = {
  paymentId: string;
  displayName: string;
  profileImage: string | null;
};

/** Payer preview returned from verify — same fields as session payer. */
export type PayerPreview = PaymentSessionPayer;

export type VerifyPayerResponse = {
  items: PayerPreview[];
};

export type PaymentSession = {
  sessionId: string;
  amount: string;
  currency: 'USD';
  status: PaymentSessionStatus;
  successUrl: string | null;
  failureUrl: string | null;
  businessName: string;
  transactionId: string | null;
  transactionStatus: string | null;
  payer: PaymentSessionPayer | null;
  /** Idle shelf — customer must start checkout before this. */
  expiresAt: string;
  /** Hard max — session + client token expire here. */
  absoluteExpiresAt: string;
  phase: PaymentSessionPhase;
  checkoutMethod: ApiCheckoutMethod | null;
  /** Set after bind-policy — `null` = full chooser when `mobileAppPolicy` is set. */
  checkoutPolicy: BoundCheckoutPolicy;
  /** Set after bind-policy succeeds; null until then. */
  mobileAppPolicy: ApiMobileAppPolicy | null;
  paymentStartAt: string | null;
  /** Pay window countdown — only meaningful when phase is PAYING. */
  paymentExpiryAt: string | null;
  createdAt: string;
};

export type ProblemJson = {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  code?: string;
  correlationId?: string;
  errors?: Record<string, string[]>;
};

import type { ZippyEnvironment } from './config/environments';

export type { ZippyEnvironment };

export type ZippyTheme = {
  primary?: string;
  primaryForeground?: string;
  background?: string;
  surface?: string;
  text?: string;
  textMuted?: string;
  border?: string;
  error?: string;
  success?: string;
  borderRadius?: string;
  fontFamily?: string;
  mode?: 'light' | 'dark' | 'auto';
};

export type EmbedMode = 'inline' | 'modal';

export type ZippyPayConfig = {
  environment: ZippyEnvironment;
  sessionId: string;
  clientToken: string;
  mode?: EmbedMode;
  container?: HTMLElement | string;
  theme?: ZippyTheme;
  /** When omitted, customer can choose QR or Manual. */
  checkoutPolicy?: CheckoutPolicy;
  /** Phone-only app handoff. Ignored on non-phone devices. */
  mobileApp?: ZippyMobileAppConfig;
  locale?: 'en';
  redirectOnComplete?: boolean;
  redirectOnFailure?: boolean;
  closeOnBackdropClick?: boolean;
  closeOnEscape?: boolean;
  onReady?: (session: PaymentSession) => void;
  onSessionUpdate?: (session: PaymentSession) => void;
  onComplete?: (session: PaymentSession) => void;
  onExpired?: (session: PaymentSession) => void;
  onError?: (error: ZippyPayError) => void;
  onClose?: () => void;
};

export type CheckoutMethod = 'qr' | 'manual' | 'mobileApp';

export type CheckoutPhase =
  | 'loading'
  | 'choose-method'
  | 'qr-active'
  | 'mobile-app-active'
  | 'manual-entry'
  | 'manual-picker'
  | 'manual-confirm'
  | 'awaiting'
  | 'success'
  | 'expired'
  | 'cancelled'
  | 'declined'
  | 'failed'
  | 'error'
  | 'invalid-session';


export const TERMINAL_STATUSES: readonly PaymentSessionStatus[] = [
  'COMPLETED',
  'EXPIRED',
  'CANCELLED',
  'FAILED',
] as const;

export const POLLABLE_STATUSES: readonly PaymentSessionStatus[] = [
  'CREATED',
  'AWAITING_PAYMENT',
] as const;

export class ZippyPayError extends Error {
  readonly code: string;
  readonly status: number;
  readonly detail: string;
  readonly correlationId?: string;

  constructor(problem: ProblemJson) {
    const detail = problem.detail ?? problem.title ?? 'An unexpected error occurred';
    super(detail);
    this.name = 'ZippyPayError';
    this.code = problem.code ?? 'unknown';
    this.status = problem.status ?? 500;
    this.detail = detail;
    this.correlationId = problem.correlationId;
  }
}

export type ZippyPayCheckoutInstance = {
  open(): void;
  close(): void;
  destroy(): void;
};
